/**
 * What a list may show while a staff member views as a client (M4S-5757,
 * docs/pdlc/view-as-client-lists.md §3).
 *
 * Staff are not scoped by the API: a staff token lists every client's projects,
 * surveys and crosstabs. View-as lives only in this tab, so a list shows the
 * client's items only when it asks for them. This is the one place that says
 * which ids to ask for, and when they are known.
 *
 * Outside View-as nothing is narrowed: the ids are null and the list fetches
 * what the caller can see, as it always has. Under View-as a list must wait for
 * its ids (`sfReady` / `orgReady`): an empty filter lists every client.
 */

export interface ViewAsListScope {
    /** A staff member is viewing as a client in this tab. */
    active: boolean;
    /** The client's Salesforce account ids, or null outside View-as. */
    sfAccountIds: readonly string[] | null;
    /** The client's Studio org ids, or null outside View-as. */
    orgIds: readonly number[] | null;
    /** A list filtered by Salesforce account may fetch. */
    sfReady: boolean;
    /** A list filtered by Studio org may fetch. */
    orgReady: boolean;
    /** For query keys, so one client's list is never served as another's. */
    key: string;
}

export const NO_VIEW_AS_SCOPE: ViewAsListScope = {
    active: false,
    sfAccountIds: null,
    orgIds: null,
    sfReady: true,
    orgReady: true,
    key: 'all',
};

export function viewAsListScope({
    isViewingAsClient,
    sfAccountIds,
    orgIds,
}: {
    isViewingAsClient: boolean;
    sfAccountIds: readonly string[];
    orgIds: readonly number[];
}): ViewAsListScope {
    if (!isViewingAsClient) return NO_VIEW_AS_SCOPE;
    return {
        active: true,
        sfAccountIds,
        orgIds,
        sfReady: sfAccountIds.length > 0,
        orgReady: orgIds.length > 0,
        key: `view-as:${orgIds.join(',')}|${sfAccountIds.join(',')}`,
    };
}
